import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Mail, Pencil, Phone, Plus, Trash2, UserRound } from 'lucide-react';
import { Page } from '@shared/ui/containers/Page';
import { Card } from '@shared/ui/containers/Card';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Banner } from '@shared/ui/feedback/Banner';
import { Button } from '@shared/ui/primitives/Button';
import { TextAreaField, TextInputField } from '@shared/ui/forms/FormFields';
import { toast } from '@shared/ui/feedback/Toast';
import {
  createMemberContact,
  deleteMemberContact,
  fetchMemberContacts,
  updateMemberContact,
  type MemberContact,
} from './memberContactsApi';

type Tab = 'active' | 'archived';

export function MyVisitors(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('active');
  const [editing, setEditing] = useState<MemberContact | null>(null);
  const [adding, setAdding] = useState(false);

  const contactsQuery = useQuery({ queryKey: ['member-contacts'], queryFn: fetchMemberContacts });
  const contacts = contactsQuery.data ?? [];
  const displayed = contacts.filter((contact) =>
    tab === 'active' ? contact.is_active !== false : contact.is_active === false,
  );

  const deleteMutation = useMutation({
    mutationFn: deleteMemberContact,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['member-contacts'] });
      toast.success('Visitor removed');
    },
    onError: (error) => toast.danger("Couldn't remove visitor", error instanceof Error ? error.message : undefined),
  });

  return (
    <Page>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <span className="eyebrow">Access</span>
          <h1 className="display-serif text-display-xl text-ink mt-1">My visitors</h1>
          <p className="mt-1 text-body-sm text-ink-secondary">
            Keep regular visitors ready for quick access invites.
          </p>
        </div>
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" strokeWidth={2} />
          Add
        </Button>
      </div>

      <div className="mb-5 flex gap-1 rounded-pill bg-sunken p-1">
        {(['active', 'archived'] as Tab[]).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            className={
              'flex-1 rounded-pill px-3 py-2 text-body-sm capitalize transition-colors duration-fast ' +
              (tab === value ? 'bg-card text-ink shadow-card' : 'text-ink-secondary')
            }
          >
            {value}
          </button>
        ))}
      </div>

      {contactsQuery.isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : contactsQuery.isError ? (
        <ErrorState onRetry={() => contactsQuery.refetch()} />
      ) : displayed.length === 0 ? (
        <EmptyState
          icon={<UserRound className="h-7 w-7" strokeWidth={1.5} />}
          title={tab === 'active' ? 'No saved visitors yet.' : 'No archived visitors.'}
          description="Add frequent visitors once, then invite them quickly next time."
          action={<Button onClick={() => setAdding(true)}>Add visitor</Button>}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {displayed.map((contact) => (
            <Card key={contact.member_contact_id} noGrain className="p-4">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-primary-50 text-primary-700">
                  <UserRound className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-display text-headline text-ink truncate">{contact.display_name}</p>
                      <p className="mt-0.5 text-body-sm text-ink-secondary truncate">
                        {contactSubtitle(contact)}
                      </p>
                    </div>
                    <span className="rounded-pill bg-success/10 px-2 py-1 text-caption font-semibold text-success">
                      {contact.is_active === false ? 'Archived' : 'Active'}
                    </span>
                  </div>

                  {contact.notes ? (
                    <p className="mt-2 line-clamp-2 text-caption text-ink-muted">{contact.notes}</p>
                  ) : null}

                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/app/access-invites/new?contact_id=${contact.member_contact_id}`)}
                    >
                      Invite
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditing(contact)}>
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (window.confirm(`Remove ${contact.display_name} from your visitors?`)) {
                          deleteMutation.mutate(contact.member_contact_id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {adding || editing ? (
        <VisitorSheet
          contact={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
        />
      ) : null}
    </Page>
  );
}

function VisitorSheet({
  contact,
  onClose,
}: {
  contact: MemberContact | null;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(contact?.display_name ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [nationalId, setNationalId] = useState(contact?.national_id ?? '');
  const [notes, setNotes] = useState(contact?.notes ?? '');
  const [isActive, setIsActive] = useState(contact?.is_active !== false);
  const [error, setError] = useState('');

  const saveMutation = useMutation({
    mutationFn: () => {
      const name = displayName.trim();
      const nextPhone = phone.trim() || null;
      const nextEmail = email.trim() || null;
      if (!name) throw new Error('Enter the visitor name.');
      if (!nextPhone && !nextEmail) {
        throw new Error('Provide at least a phone number or email.');
      }
      const payload = {
        display_name: name,
        phone: nextPhone,
        email: nextEmail,
        national_id: nationalId.trim() || null,
        notes: notes.trim() || null,
      };
      return contact
        ? updateMemberContact(contact.member_contact_id, { ...payload, is_active: isActive })
        : createMemberContact(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['member-contacts'] });
      toast.success(contact ? 'Visitor updated' : 'Visitor added');
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save the visitor.'),
  });

  return (
    <BottomSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={contact ? 'Edit visitor' : 'Add visitor'}
      description="Saved visitors can be selected when creating access invites."
    >
      <div className="flex flex-col gap-4 pb-4">
        {error ? <Banner variant="danger" title="Check visitor details" description={error} /> : null}
        <TextInputField
          label="Visitor name"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          startAdornment={<UserRound className="h-4 w-4" strokeWidth={1.5} />}
        />
        <TextInputField
          label="Phone"
          type="tel"
          inputMode="tel"
          value={phone ?? ''}
          onChange={(event) => setPhone(event.target.value)}
          startAdornment={<Phone className="h-4 w-4" strokeWidth={1.5} />}
          optional
        />
        <TextInputField
          label="Email"
          type="email"
          value={email ?? ''}
          onChange={(event) => setEmail(event.target.value)}
          startAdornment={<Mail className="h-4 w-4" strokeWidth={1.5} />}
          optional
        />
        <TextInputField
          label="National ID"
          value={nationalId ?? ''}
          onChange={(event) => setNationalId(event.target.value)}
          optional
        />
        <TextAreaField
          label="Notes"
          value={notes ?? ''}
          onChange={(event) => setNotes(event.target.value)}
          optional
        />
        {contact ? (
          <label className="flex items-center gap-3 rounded-md bg-card p-3 hairline">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
              className="h-4 w-4 accent-[color:var(--primary-600)]"
            />
            <span className="flex items-center gap-2 text-body text-ink">
              <Archive className="h-4 w-4 text-ink-muted" strokeWidth={1.5} />
              Active visitor
            </span>
          </label>
        ) : null}
        <div className="mt-2 grid grid-cols-2 gap-3">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            Save visitor
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

function contactSubtitle(contact: MemberContact): string {
  const parts = [contact.phone, contact.email].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'No contact details';
}
